// Bulk import data layer — Blueprint §2 (screen 6 Import) + §5 Phase 5 / F-IMPORT-*.
// Ports the SEMANTICS of legacy downloadTemplate / handleImportFile /
// validateImportRows / executeImport (v20 lines ~5539-5816) onto the 2.0 store +
// envelope + contract infrastructure, with the vendored SheetJS global (XLSX).
//
// Two import MODES (legacy importMode):
//   'new'    — physical consent forms → rpc_bulk_import_consents → land in the
//              New consents review queue (R-025: staff-entered, so each row will
//              require a verifier + scanned consent BEFORE it can be promoted).
//   'legacy' — existing programme patients → rpc_bulk_import_legacy → land
//              directly in cases as a Triage queue (ownerless — drained via the
//              Legacy pool bulk-assign).
//
// The PURE parts (header normalisation, row cleaning, dedupe keys, per-row
// validation against the Reference vocabulary, programme summary, the template
// sheet AoAs) live here with no DOM and no XLSX and are unit-tested. The impure
// parts (parseWorkbook, buildTemplateWorkbook/writeTemplate, executeBulkImport)
// touch the vendored XLSX global and the Supabase RPCs.
//
// COMPAT (Rule 2): the import RPCs are called EXACTLY as v_20 calls them —
// `.rpc(name, { p_rows: chunk })`, chunked at 300, reading back
// {imported, total, errors[], duplicates[]}. The cleaned row shape sent up is
// byte-identical to v_20's so a v_20 tab and a 2.0 tab import into the same
// tables identically.

import { store, SLICES } from '../state/store.js';
import { getClient, ok, fail } from '../db/client.js';
import { RPCS } from '../db/contract.js';
import { csvSafe } from '../ui/esc.js';
import { logError, newRef } from '../errors.js';
import { KENYA_COUNTIES, matchCounty } from './counties.js';

// Legacy chunk size for executeImport — each chunk commits before the next runs
// so server-side dedupe holds across chunks (v20 CHUNK = 300).
export const IMPORT_CHUNK = 300;

// ─── Reference vocabulary (the template's Reference sheet) ──────────────────
// The valid values every row is re-validated against. Programmes are DB-driven
// (untrusted DB data, §3) so they are NOT hard-coded here — the caller passes
// the loaded catalogue's codes in; everything else is a fixed system vocabulary
// that matches the DB CHECK constraints / enums.

/** Sexes accepted on the consent/patient record (legacy validSex). */
export const VALID_SEX = Object.freeze(['female', 'male', 'intersex', 'prefer_not_to_say']);

/** Approximate-stage hints for a LEGACY patient (template Reference sheet). */
export const VALID_STAGES = Object.freeze([
  'enrolled', 'welcome', 'sha_pending', 'sha_approved', 'delivery', 'active', 'adherence', 'refill', 'review', 'unknown',
]);

/** Status hints for a LEGACY patient (template Reference sheet). */
export const VALID_STATUS_HINTS = Object.freeze([
  'active', 'possibly_deceased', 'dropped_off', 'lost_contact', 'side_effects', 'completed', 'unknown',
]);

// The 47 Kenyan counties + the free-text resolver now live in data/counties.js
// (R-032: the dropdown and the spreadsheet validator must share ONE list, or
// they will disagree about what a county is). Re-exported here because the
// template's Reference sheet and every existing caller import them from this
// module — the list itself is not duplicated.
export { KENYA_COUNTIES, matchCounty };

/** Fallback programme codes for the template when the catalogue hasn't loaded
 *  (legacy hard-coded set). Live validation prefers the DB catalogue. */
export const FALLBACK_PROGRAMME_CODES = Object.freeze(['ABEECARE', 'ENZACARE', 'PALBOCARE']);

/** True for null/undefined only (empty string is a real, keepable value). */
function isNil(v) {
  return v === null || v === undefined;
}

/** Normalise any label for a case-insensitive vocabulary match. */
function norm(v) {
  return String(isNil(v) ? '' : v).trim().toLowerCase();
}

// ─── Header normalisation + column mapping (legacy handleImportFile) ─────────
// The template ships human headers ("Patient Full Name *", "Phone (+254) *");
// normalise each to the canonical field key the RPC expects. Ported verbatim
// from the legacy header-cleaning + variant map so the payload matches v_20.

/** Canonical field key for a raw spreadsheet header. Pure. */
export function normalizeHeader(raw) {
  const clean = String(raw || '')
    .replace(/\s*\*\s*$/, '')
    .trim()
    .toLowerCase()
    .replace(/\s+/g, '_')
    .replace(/[()/]/g, '')
    // Strip the "+254"/"254" country-code hint the phone header carries
    // ("Phone (+254)" → "phone_+254" → "phone"). The legacy build used
    // literal-ellipsis replaces here that never matched a real header.
    .replace(/_?\+?254$/, '')
    .replace('full_name', 'name')
    .replace('patient_', '')
    .replace('town__area', 'location')
    .replace('hospital__clinic', 'treating_facility')
    .replace('doctor_', 'physician_');
  return (
    /** @type {Record<string,string>} */ ({
      patient_full_name: 'patient_name', name: 'patient_name',
      programme: 'programme', program: 'programme',
      national_id__passport: 'national_id', id_number: 'national_id', id: 'national_id',
      date_of_birth: 'dob', dob: 'dob',
      sex: 'sex',
      phone: 'patient_phone',
      alt_phone: 'patient_alt_phone', backup_phone: 'patient_alt_phone',
      email: 'patient_email',
      county: 'county',
      town__area: 'location', location: 'location', area: 'location',
      caregiver_name: 'caregiver_name',
      caregiver_phone: 'caregiver_phone',
      doctor_name: 'physician_name', physician_name: 'physician_name',
      doctor_phone: 'physician_phone', physician_phone: 'physician_phone',
      hospital_or_clinic: 'treating_facility', treating_facility: 'treating_facility', facility: 'treating_facility',
      consent_date: 'consent_date',
      notes: 'notes',
      approximate_stage: 'approximate_stage', stage: 'approximate_stage',
      treatment_start_date: 'treatment_start_date',
      current_cycle: 'current_cycle', 'current_cycle_#': 'current_cycle', cycle: 'current_cycle',
      last_contact_date: 'last_contact_date',
      status_hint: 'status_hint', status: 'status_hint',
      migration_notes: 'migration_notes',
    })[clean] || clean
  );
}

/**
 * Map one raw sheet-row object (human headers) → canonical field keys. Pure.
 * @param {Record<string, any>} raw
 * @returns {Record<string, any>}
 */
export function normalizeImportRow(raw) {
  /** @type {Record<string, any>} */
  const o = {};
  for (const k of Object.keys(raw || {})) o[normalizeHeader(k)] = raw[k];
  return o;
}

/**
 * Normalise every raw row and drop blank/placeholder rows (a real name of ≥2
 * chars is the legacy "is this a data row" test). Pure.
 * @param {Array<Record<string, any>>} rawRows
 * @returns {Array<Record<string, any>>}
 */
export function mapRows(rawRows) {
  return (rawRows || [])
    .map(normalizeImportRow)
    .filter((r) => r.patient_name && String(r.patient_name).trim().length > 1);
}

// ─── Row cleaning for the wire (legacy executeImport pre-send cleanup) ───────

/** Strip a Kenyan phone to its national 9 digits (leading 0 / +254 removed). */
export function normalizePhone(v) {
  return String(isNil(v) ? '' : v).replace(/\s/g, '').replace(/^\+254/, '').replace(/^0/, '');
}

/** The four date columns the template carries; every one is cast `::date` by
 *  the import RPCs. */
export const DATE_FIELDS = Object.freeze(['dob', 'consent_date', 'treatment_start_date', 'last_contact_date']);

/**
 * Is a date cell acceptable for the wire? Pure. A real Date (SheetJS gives one
 * for a date-formatted cell, and cleanRow turns it into ISO) or a string in
 * the template's own `YYYY-MM-DD` format — and nothing else.
 *
 * WHY THIS IS STRICT: the RPC runs `nullif(v_row->>'dob','')::date`. Under
 * PostgreSQL's default `DateStyle = ISO, MDY` a nurse's `12/04/1975` is
 * accepted as 4 December — a silently wrong date of birth on a patient record
 * — and `25/04/1975` throws, which fails the WHOLE 300-row chunk. Neither is
 * something the preview should call "valid". Blank means "not recorded" and is
 * left to the required-field rules.
 * @param {any} v the raw cell (pre-clean)
 * @returns {boolean}
 */
export function isAcceptableDate(v) {
  if (isNil(v) || v === '') return true;
  if (v instanceof Date) return !isNaN(v.getTime());
  const s = String(v).trim();
  if (!s) return true;
  const m = /^(\d{4})-(\d{2})-(\d{2})$/.exec(s);
  if (!m) return false;
  const y = Number(m[1]);
  const mo = Number(m[2]);
  const d = Number(m[3]);
  const dt = new Date(y, mo - 1, d);
  return dt.getFullYear() === y && dt.getMonth() === mo - 1 && dt.getDate() === d;
}

/** A Date/any → 'YYYY-MM-DD' (or trimmed string, or '' ). */
function toIsoDate(v) {
  if (v instanceof Date && !isNaN(v.getTime())) return v.toISOString().slice(0, 10);
  return String(isNil(v) ? '' : v).trim();
}

/**
 * Clean one mapped row into the byte-compatible payload v_20 sends: national
 * phones, lower-cased programme + sex, ISO dates, integer cycle. Pure — returns
 * a NEW object. Empty strings are preserved (the RPC treats them as null).
 * @param {Record<string, any>} row a mapRows() row
 * @returns {Record<string, any>}
 */
export function cleanRow(row) {
  const o = Object.assign({}, row);
  for (const f of ['patient_phone', 'patient_alt_phone', 'caregiver_phone', 'physician_phone']) {
    if (o[f]) o[f] = normalizePhone(o[f]);
  }
  if (o.programme) o.programme = String(o.programme).trim().toLowerCase();
  if (o.sex) o.sex = String(o.sex).trim().toLowerCase();
  for (const f of ['dob', 'consent_date', 'treatment_start_date', 'last_contact_date']) {
    if (!isNil(o[f]) && o[f] !== '') o[f] = toIsoDate(o[f]);
  }
  if (o.current_cycle) o.current_cycle = parseInt(o.current_cycle, 10) || 1;
  return o;
}

/** Clean a whole set. @param {Array<Record<string, any>>} rows */
export function cleanRows(rows) {
  return (rows || []).map(cleanRow);
}

// ─── In-file dedupe (legacy dedupe key) ──────────────────────────────────────

/**
 * The identity key for an in-file duplicate: last-9 phone, else lower national
 * id, else lower name. '' when there is nothing to key on (never grouped).
 * @param {Record<string, any>} row a mapped (pre-clean) or clean row
 * @returns {string}
 */
export function dedupeKey(row) {
  const digits = String((row && row.patient_phone) || '').replace(/\D/g, '');
  if (digits.length >= 9) return 'p:' + digits.slice(-9);
  const id = norm(row && row.national_id);
  if (id) return 'i:' + id;
  const name = norm(row && row.patient_name);
  return name ? 'n:' + name : '';
}

/**
 * Indices of rows that repeat an earlier row's dedupe key (the 2nd+ occurrence).
 * The server dedupes too, but flagging in-file collisions warns the user before
 * they upload. Pure.
 * @param {Array<Record<string, any>>} rows
 * @returns {Set<number>}
 */
export function findInFileDuplicates(rows) {
  const seen = new Set();
  const dup = new Set();
  (rows || []).forEach((r, i) => {
    const k = dedupeKey(r);
    if (!k) return;
    if (seen.has(k)) dup.add(i);
    else seen.add(k);
  });
  return dup;
}

// ─── Per-row validation against the Reference vocabulary ─────────────────────

/**
 * @typedef {Object} ImportRefCtx
 * @property {Set<string>} [programmes] valid programme codes (lower-cased); empty/absent = only require non-empty
 */

/**
 * Validate one MAPPED row (pre-clean) for a mode against the Reference
 * vocabulary. Returns a list of human messages ([] when the row is clean).
 * Re-validates programme, sex, phone, county (both modes) + stage & status hint
 * (legacy). Pure.
 * @param {Record<string, any>} row
 * @param {'new'|'legacy'} mode
 * @param {ImportRefCtx} [ctx]
 * @returns {string[]}
 */
export function validateRow(row, mode, ctx) {
  const errs = [];
  const progCodes = (ctx && ctx.programmes) || null;

  if (!row.patient_name || String(row.patient_name).trim().length < 3) {
    errs.push('Patient name is missing or too short');
  }

  const prog = norm(row.programme);
  if (!prog) {
    errs.push('Programme is required');
  } else if (progCodes && progCodes.size && !progCodes.has(prog)) {
    errs.push(`Programme "${row.programme}" is not a known programme code`);
  }

  if (row.sex && !VALID_SEX.includes(norm(row.sex))) {
    errs.push('Sex must be female, male, intersex, or prefer_not_to_say');
  }

  const phone = normalizePhone(row.patient_phone);
  if (phone && phone.length !== 9) {
    errs.push(`Phone should be 9 digits after +254 (got ${phone.length})`);
  }

  // County: required for NEW patients (the consent form captures it, so a blank
  // means someone skipped a field), but OPTIONAL for LEGACY imports. The 2024-26
  // paper registers this mode exists for simply never recorded a county — 700+
  // real ABEECARE patients carry a processing centre and sometimes a town, and
  // rejecting them would block the entire migration to invent data nobody has.
  // A legacy blank imports as an honest gap (the DQ completeness tile counts
  // it); a legacy county that IS present must still be a real county.
  if (!norm(row.county)) {
    if (mode !== 'legacy') errs.push('County is required');
  } else if (!matchCounty(row.county)) {
    errs.push(`County "${row.county}" is not a recognised Kenyan county`);
  }

  for (const f of DATE_FIELDS) {
    if (!isAcceptableDate(row[f])) {
      errs.push(`${DATE_LABELS[f]} "${row[f]}" must be written YYYY-MM-DD (e.g. 1975-04-12)`);
    }
  }

  if (mode === 'legacy') {
    const stage = norm(row.approximate_stage);
    if (!stage) errs.push('Approximate stage is required for legacy patients');
    else if (!VALID_STAGES.includes(stage)) errs.push(`Approximate stage "${row.approximate_stage}" is not valid`);

    const hint = norm(row.status_hint);
    if (!hint) errs.push('Status hint is required for legacy patients');
    else if (!VALID_STATUS_HINTS.includes(hint)) errs.push(`Status hint "${row.status_hint}" is not valid`);

    if (!norm(row.migration_notes) && !hint) {
      errs.push('Legacy patients need either migration notes or a status hint');
    }
  }
  return errs;
}

const DATE_LABELS = Object.freeze({
  dob: 'Date of birth',
  consent_date: 'Consent date',
  treatment_start_date: 'Treatment start date',
  last_contact_date: 'Last contact date',
});

/**
 * Validate a whole mapped set. Returns per-row errors (1-indexed to match the
 * spreadsheet row the user sees), a flat message list for the preview, and the
 * in-file duplicate indices. Pure — the single source of truth the view and the
 * Confirm gate both read (a badge can never disagree with the block).
 * @param {Array<Record<string, any>>} rows mapped rows
 * @param {'new'|'legacy'} mode
 * @param {ImportRefCtx} [ctx]
 * @returns {{ rowErrors: Array<{row: number, messages: string[]}>, messages: string[],
 *   dupIndices: Set<number>, validCount: number, errorCount: number }}
 */
export function validateImportRows(rows, mode, ctx) {
  const list = rows || [];
  const dupIndices = findInFileDuplicates(list);
  const rowErrors = [];
  const messages = [];
  list.forEach((r, i) => {
    const msgs = validateRow(r, mode, ctx);
    if (msgs.length) {
      rowErrors.push({ row: i + 1, messages: msgs });
      for (const m of msgs) messages.push(`Row ${i + 1}: ${m}`);
    }
  });
  return {
    rowErrors,
    messages,
    dupIndices,
    validCount: list.length - rowErrors.length,
    errorCount: rowErrors.length,
  };
}

/**
 * Rows whose county blocks the import (missing, or not one of the 47) — the
 * model behind the preview's county-fix panel (R-032).
 *
 * This is the ONE place in the app where a county is entered by hand, so it is
 * where the free-text box becomes a dropdown. It reports EVERY affected row,
 * not only the rows the truncated preview table happens to show, because a
 * fixer that silently covered the first 15 of 60 bad rows would leave the user
 * clicking Confirm on an import that is still blocked with no visible reason.
 *
 * Pure. `typed` is the value exactly as the spreadsheet had it (may be '');
 * `suggestion` is matchCounty()'s guess or null — offered, never applied.
 * @param {Array<Record<string, any>>} rows mapped rows
 * @returns {Array<{index: number, typed: string, suggestion: string|null}>}
 */
export function countyFixRows(rows) {
  /** @type {Array<{index: number, typed: string, suggestion: string|null}>} */
  const out = [];
  (rows || []).forEach((r, i) => {
    const typed = String((r && r.county) || '').trim();
    if (typed && matchCounty(typed)) return; // resolvable → not a blocker
    out.push({ index: i, typed, suggestion: typed ? matchCounty(typed) : null });
  });
  return out;
}

/**
 * Programme tallies for the preview summary (legacy programmes{} loop). Pure.
 * @param {Array<Record<string, any>>} rows
 * @returns {Array<{code: string, count: number}>}
 */
export function summarizeProgrammes(rows) {
  /** @type {Map<string, number>} */
  const m = new Map();
  for (const r of rows || []) {
    const p = String((r && r.programme) || '—').trim().toUpperCase() || '—';
    m.set(p, (m.get(p) || 0) + 1);
  }
  return Array.from(m, ([code, count]) => ({ code, count }));
}

/** Programme codes (lower-cased) from the loaded catalogue, as a Set for validation. */
export function programmeCodeSet() {
  const prog = store.get(SLICES.PROGRAMMES) || {};
  const byCode = prog.byCode || {};
  const set = new Set();
  for (const k of Object.keys(byCode)) set.add(norm(k));
  for (const p of prog.list || []) if (p && p.code) set.add(norm(p.code));
  return set;
}

/** Programme codes (upper-cased, for the template Reference sheet), catalogue-first. */
export function templateProgrammeCodes() {
  const set = programmeCodeSet();
  if (set.size) return Array.from(set, (c) => c.toUpperCase());
  return FALLBACK_PROGRAMME_CODES.slice();
}

// ─── Template sheets as pure AoAs (legacy downloadTemplate) ──────────────────

/**
 * The three template sheets as array-of-arrays + column widths — pure so the
 * exact header/example/reference content is unit-testable independently of the
 * XLSX writer. buildTemplateWorkbook() turns these into a workbook.
 * @param {string[]} [programmeCodes] valid programme codes for the Reference sheet
 * @returns {{ newSheet: {name: string, aoa: any[][], cols: number[]},
 *   legacySheet: {name: string, aoa: any[][], cols: number[]},
 *   refSheet: {name: string, aoa: any[][], cols: number[]} }}
 */
export function buildTemplateSheets(programmeCodes) {
  // Programme codes come from the DB catalogue (untrusted per §3). A code beginning
  // with = + - @ (or tab/CR) would be run as a live formula when the nurse opens the
  // downloaded template — neutralise every DB-sourced cell through csvSafe first.
  const progList = (programmeCodes && programmeCodes.length ? programmeCodes : FALLBACK_PROGRAMME_CODES).map(csvSafe).join(', ');

  const newHeaders = ['Patient Full Name *', 'Programme *', 'National ID / Passport *', 'Date of Birth *', 'Sex *', 'Phone (+254) *', 'Alt Phone', 'Email', 'County *', 'Town / Area', 'Caregiver Name', 'Caregiver Phone', 'Doctor Name', 'Doctor Phone', 'Hospital / Clinic', 'Consent Date *', 'Notes'];
  const newExample = ['Mary Anne Wanjiku', progListFirst(programmeCodes), '12345678', '1975-04-12', 'female', '0712345678', '0720999111', 'mary@example.com', 'Nairobi City', 'Westlands, Sarit area', 'James Wanjiku', '0722555444', 'Dr. Kimani Otieno', '0733222111', 'Aga Khan University Hospital', '2025-01-15', 'Physical consent ref: PC-001'];

  const legacyHeaders = ['Patient Full Name *', 'Programme *', 'National ID / Passport *', 'Date of Birth *', 'Sex *', 'Phone (+254) *', 'Alt Phone', 'Email', 'County *', 'Town / Area', 'Caregiver Name', 'Caregiver Phone', 'Doctor Name', 'Doctor Phone', 'Hospital / Clinic', 'Approximate Stage *', 'Treatment Start Date', 'Current Cycle #', 'Last Contact Date', 'Status Hint *', 'Migration Notes *'];
  const legacyExample = ['Grace Nyambura Kariuki', progListFirst(programmeCodes), 'A11223344', '1968-09-22', 'female', '0712111222', '', 'grace@example.com', 'Kiambu', 'Ruiru, near Spur Mall', 'Peter Kariuki', '0722333444', 'Dr. Aisha Mwangi', '0733555666', 'Nairobi Hospital', 'delivery', '2024-01-20', '8', '2025-03-15', 'active', 'Last delivery March 2025, cycle 8, caregiver reports mild nausea'];

  const refAoa = [
    ['FIELD', 'VALID VALUES'],
    ['Programme', progList],
    ['Sex', VALID_SEX.join(', ')],
    ['Approximate Stage', VALID_STAGES.join(', ')],
    ['Status Hint', VALID_STATUS_HINTS.join(', ')],
    ['Counties', KENYA_COUNTIES.join(', ')],
    [''],
    ['INSTRUCTIONS'],
    ['1. Fill Sheet 1 (New Patients) for physical consent imports — these go to New consents for review'],
    ['2. Fill Sheet 2 (Legacy Patients) for existing programme patients — these go to the Triage queue'],
    ['3. Columns marked * are required'],
    ['4. Phones: include the leading 0 (e.g. 0712345678) or just 9 digits (712345678)'],
    ['5. Dates: use YYYY-MM-DD format (e.g. 1975-04-12)'],
    ['6. Save as .xlsx and upload in the Import section of the app'],
    ['7. New-patient rows are staff-entered: a verifier + the scanned consent are required at review before promotion'],
  ];

  return {
    newSheet: { name: 'New Patients', aoa: [newHeaders, newExample], cols: [32, 20, 22, 16, 12, 20, 18, 26, 18, 22, 22, 18, 22, 18, 26, 16, 36] },
    legacySheet: { name: 'Legacy Patients', aoa: [legacyHeaders, legacyExample], cols: [32, 20, 22, 16, 12, 20, 18, 26, 18, 22, 22, 18, 22, 18, 26, 22, 20, 16, 18, 22, 44] },
    refSheet: { name: 'Reference', aoa: refAoa, cols: [22, 120] },
  };
}

/** First programme code for the example rows (keeps the template self-consistent).
 *  csvSafe'd — the code is DB-sourced and lands in a worksheet cell. */
function progListFirst(codes) {
  return csvSafe(codes && codes.length ? codes[0] : FALLBACK_PROGRAMME_CODES[0]);
}

// ─── XLSX global accessor (vendored IIFE, like db/client.js reads supabase) ──

/**
 * The vendored SheetJS global, or null if the script did not load. NEVER throws
 * — callers surface an honest "a script failed to load" message (legacy guard).
 * @returns {any|null}
 */
export function getXLSX() {
  const x = /** @type {any} */ (globalThis).XLSX;
  return x && x.utils ? x : null;
}

// ─── Parse an uploaded workbook (impure — XLSX.read) ─────────────────────────

/**
 * Parse an .xlsx ArrayBuffer for a mode into mapped, filtered rows. Reads the
 * mode's named sheet (falls back to the first sheet), taking ROW 0 as the header
 * row (the template ships headers in row 0 + one example row beneath). Returns
 * an envelope-ish result so the view can branch without a try/catch. NEVER
 * throws. (Legacy passed range:1, which treats row 1 as the header and silently
 * dropped every real column — a round-trip bug against its own template.)
 * @param {ArrayBuffer|Uint8Array} data
 * @param {'new'|'legacy'} mode
 * @returns {{ok: true, rows: Array<Record<string, any>>}|{ok: false, reason: string}}
 */
export function parseWorkbook(data, mode) {
  const XLSX = getXLSX();
  if (!XLSX) return { ok: false, reason: 'The spreadsheet reader failed to load — reload when online and try again' };
  try {
    const wb = XLSX.read(data, { type: 'array', cellDates: true });
    const sheetName = mode === 'legacy' ? 'Legacy Patients' : 'New Patients';
    const ws = wb.Sheets[sheetName] || wb.Sheets[wb.SheetNames[0]];
    if (!ws) return { ok: false, reason: `Could not find a "${sheetName}" sheet in the file` };
    const raw = XLSX.utils.sheet_to_json(ws, { defval: '' });
    if (!raw || !raw.length) return { ok: false, reason: 'No data rows found in the file' };
    const rows = mapRows(raw);
    if (!rows.length) return { ok: false, reason: 'No valid rows found after filtering blank rows' };
    return { ok: true, rows };
  } catch (e) {
    const err = /** @type {any} */ (e);
    return { ok: false, reason: 'Could not read the file: ' + (err && err.message ? err.message : 'unknown error') };
  }
}

// ─── Build + write the template workbook (impure — XLSX.utils) ───────────────

/**
 * Build the three-sheet template workbook from the pure AoAs. Returns null if
 * XLSX is unavailable (caller shows the honest load-fail message).
 * @param {string[]} [programmeCodes]
 * @returns {any|null} a SheetJS workbook
 */
export function buildTemplateWorkbook(programmeCodes) {
  const XLSX = getXLSX();
  if (!XLSX) return null;
  const { newSheet, legacySheet, refSheet } = buildTemplateSheets(programmeCodes);
  const wb = XLSX.utils.book_new();
  for (const s of [newSheet, legacySheet, refSheet]) {
    const ws = XLSX.utils.aoa_to_sheet(s.aoa);
    ws['!cols'] = s.cols.map((w) => ({ wch: w }));
    XLSX.utils.book_append_sheet(wb, ws, s.name);
  }
  return wb;
}

/**
 * Write the template to a downloaded .xlsx (legacy downloadTemplate tail).
 * @param {string[]} [programmeCodes]
 * @param {string} [filename]
 * @returns {{ok: boolean, error: any, code: string|null}}
 */
export function writeTemplate(programmeCodes, filename) {
  const XLSX = getXLSX();
  const wb = buildTemplateWorkbook(programmeCodes);
  if (!XLSX || !wb) return fail(new Error('xlsx not loaded'), 'NO_XLSX');
  try {
    XLSX.writeFile(wb, filename || 'Tumaini_Haven_Import_Template.xlsx');
    return ok(null);
  } catch (e) {
    return fail(e);
  }
}

// ─── Execute the import (impure — chunked RPC, byte-identical to v_20) ────────

/** RPC name for a mode (contract-locked). */
export function rpcForMode(mode) {
  return mode === 'legacy' ? RPCS.BULK_IMPORT_LEGACY : RPCS.BULK_IMPORT_CONSENTS;
}

/**
 * Send the cleaned rows to the mode's import RPC in chunks, aggregating the
 * server's {imported, total, errors[], duplicates[]} across chunks. Each chunk
 * commits before the next (server dedupe holds across the boundary). A per-chunk
 * RPC error is captured into `errors` and the loop CONTINUES (legacy behaviour —
 * a late chunk failing must not discard the rows already imported), but is also
 * surfaced as the envelope error when NOTHING imported. NEVER throws.
 * @param {Array<Record<string, any>>} rows mapped rows (cleaned here)
 * @param {'new'|'legacy'} mode
 * @param {{client?: any, onProgress?: (done: number, total: number) => void}} [deps]
 * @returns {Promise<{ok: boolean, data: {imported: number, total: number, errors: string[], duplicates: string[]}, error: any, code: string|null}>}
 */
export async function executeBulkImport(rows, mode, deps) {
  const client = (deps && deps.client) || getClient();
  const rpc = rpcForMode(mode);
  const clean = cleanRows(rows);
  let imported = 0;
  let total = 0;
  /** @type {string[]} */
  const errors = [];
  /** @type {string[]} */
  const duplicates = [];
  /** @type {any} */
  let lastError = null;

  for (let i = 0; i < clean.length; i += IMPORT_CHUNK) {
    const chunk = clean.slice(i, i + IMPORT_CHUNK);
    /** @type {any} */
    let res;
    try {
      res = await client.rpc(rpc, { p_rows: chunk });
    } catch (e) {
      res = { data: null, error: e };
    }
    if (res && res.error) {
      lastError = res.error;
      // The driver message is logged under a TU ref for support and NEVER put
      // on screen: PostgREST/Postgres wording is schema internals, not copy a
      // nurse can act on (the same rule netErr() applies everywhere else).
      const ref = newRef();
      logError({ ref, source: 'bulkImport', name: res.error && res.error.name, code: res.error && res.error.code, message: res.error && res.error.message });
      const upto = Math.min(i + IMPORT_CHUNK, clean.length);
      errors.push(`Rows ${i + 1}–${upto} were not imported — the server rejected that batch (ref ${ref}). Fix the file or try again; the other rows are unaffected.`);
      if (deps && deps.onProgress) deps.onProgress(upto, clean.length);
      continue;
    }
    const d = res && res.data ? res.data : {};
    imported += d.imported || 0;
    total += d.total || chunk.length;
    for (const e of d.errors || []) errors.push(String(e));
    for (const dup of d.duplicates || []) duplicates.push(String(dup));
    if (deps && deps.onProgress) deps.onProgress(Math.min(i + IMPORT_CHUNK, clean.length), clean.length);
  }

  const result = { imported, total, errors, duplicates };
  // A total failure (nothing imported AND an RPC error) is an envelope error so
  // the view shows netErr, not a "0 imported" success. Partial success stays ok.
  if (imported === 0 && lastError) return Object.assign(fail(lastError), { data: result });
  return ok(result);
}

/**
 * Pure: the in-flight progress toast for one finished batch, or null when there
 * is nothing to say (review R-16). executeBulkImport reports EVERY batch,
 * rejected ones included, so the copy says "processed", not "imported" — a
 * rejected batch counted as imported would over-report, and the old view
 * condition (`total > done && total > rows.length - 1`) coupled the rule to
 * the preview's row count. After the last batch the result toast speaks.
 * @param {number} done rows in the batches finished so far
 * @param {number} total rows being sent
 * @returns {string|null}
 */
export function importProgressText(done, total) {
  if (!(typeof done === 'number' && typeof total === 'number')) return null;
  if (!(done > 0) || done >= total) return null;
  return `Importing… ${done} of ${total} rows processed`;
}
